import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { SEOHead } from "@/components/SEOHead";
import { trackConversion } from "@/lib/analytics";
import { useDemoModal } from "@/context/DemoModalContext";

const PRICING_SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Meshly",
  description:
    "Two products, one trust harness. The Meshly Intelligence Platform reconciles every system and catches the revenue that slips between them. Meshly Validate catches risky contracts before they reach your systems. Built for humans and agents.",
  provider: { "@type": "Organization", name: "Meshly", url: "https://www.meshly.ai" },
  serviceType: "Revenue trust harness",
  areaServed: "Worldwide",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Meshly pricing",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Intelligence Platform" }, price: "2900", priceCurrency: "USD" },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Validate Starter" }, price: "299", priceCurrency: "USD" },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Validate Plus" }, price: "799", priceCurrency: "USD" },
    ],
  },
};

// solid tokens only: text-foreground (primary), text-muted-foreground (tiny
// notes), text-primary (eyebrows/links), white on the blue section.
const Eyebrow = ({ children, onBlue }: { children: React.ReactNode; onBlue?: boolean }) => (
  <span className={`mb-5 inline-block font-mono-tech text-base font-semibold uppercase tracking-[0.18em] ${onBlue ? "text-white" : "text-primary"}`}>
    {children}
  </span>
);

const SectionH2 = ({ children, onBlue }: { children: React.ReactNode; onBlue?: boolean }) => (
  <h2 className={`text-4xl font-bold tracking-[-0.02em] md:text-5xl ${onBlue ? "text-white" : "text-foreground"}`}>
    {children}
  </h2>
);

type Tier = {
  name: string;
  blurb: string;
  price: string;
  cadence?: string;
  note?: string;
  features: string[];
  cta: string;
  ctaVariant?: "default" | "outline";
  highlight?: boolean;
  onClick: () => void;
};

function TierCard({ tier }: { tier: Tier }) {
  const isWord = !tier.price.startsWith("$");
  return (
    <div
      className={`flex h-full flex-col rounded-xl bg-white p-6 ${
        tier.highlight ? "border-2 border-primary" : "border border-foreground/15"
      }`}
    >
      <h3 className="text-xl font-bold tracking-tight text-foreground">{tier.name}</h3>
      <p className="mt-1.5 min-h-[2.5rem] text-sm leading-relaxed text-foreground">{tier.blurb}</p>
      <p className="mt-3 flex items-baseline gap-2">
        <span className={`font-bold tabular-nums tracking-tight text-foreground ${isWord ? "text-3xl" : "text-4xl"}`}>{tier.price}</span>
        {tier.cadence && <span className="text-base font-medium text-muted-foreground">{tier.cadence}</span>}
      </p>
      {tier.note && <p className="mt-1.5 text-xs font-medium text-muted-foreground">{tier.note}</p>}
      <ul className="mt-5 flex-1 space-y-2.5 border-t border-border pt-5">
        {tier.features.map((f, i) => (
          <li key={f} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={3} />
            <span className={i === 0 ? "font-semibold" : ""}>{f}</span>
          </li>
        ))}
      </ul>
      <Button size="default" variant={tier.ctaVariant ?? "default"} className="mt-6 w-full rounded-none text-sm font-semibold" onClick={tier.onClick}>
        {tier.cta}
      </Button>
    </div>
  );
}

const Pricing = () => {
  const navigate = useNavigate();
  const { openModal } = useDemoModal();

  useEffect(() => {
    trackConversion("pricing_page_view");
  }, []);

  const goValidate = (plan: string) => () => {
    trackConversion("pricing_plan_selected", { plan });
    navigate("/validate-signup");
  };
  const talkToUs = (ctx: string) => () => {
    trackConversion("pricing_demo_clicked", { ctx });
    openModal();
  };

  const platformTiers: Tier[] = [
    {
      name: "Platform",
      blurb: "A verification layer matched against your systems.",
      price: "$2,900",
      cadence: "/ month",
      features: [
        "Meshly Validate Plus included, up to 100 contracts/month",
        "Every number traced back to the contract, system, or decision it came from",
        "Continuous drift detection across every connected system",
        "Forward-deployed engineer, custom SLAs, and advanced compliance controls",
        "Audit-ready documentation, produced as you go",
      ],
      cta: "Start trial today",
      onClick: talkToUs("platform"),
    },
    {
      name: "Custom",
      blurb: "For finance teams with bespoke reconciliation logic, high volume, and strict audit and security requirements.",
      price: "Custom",
      note: "Scoped to your systems and volume",
      features: [
        "Everything in Platform",
        "Bespoke reconciliation logic and unlimited volume",
        "SSO/SAML, audit logs, and custom data-retention controls",
        "Dedicated team: onboarding, migration, and a named forward-deployed engineer",
        "Custom SLAs and security review",
      ],
      cta: "Contact sales",
      ctaVariant: "outline",
      onClick: talkToUs("platform-custom"),
    },
  ];

  const validateTiers: Tier[] = [
    {
      name: "Starter",
      blurb: "For teams reviewing up to 30 contracts a month.",
      price: "$299",
      cadence: "/ month",
      note: "Up to 30 contracts a month",
      features: [
        "Reads 20+ key terms from every contract",
        "Every term cited back to the clause it came from",
        "Revenue-recognition and policy checks, automatic",
        "Your own policy thresholds: payment terms, discount tiers, PO required",
        "Add 15-contract packs for $149, good for 12 months",
        "API access and priority support",
      ],
      cta: "Start free trial",
      onClick: goValidate("starter"),
    },
    {
      name: "Plus",
      blurb: "Higher volume, with advanced compliance and support.",
      price: "$799",
      cadence: "/ month",
      note: "Up to 100 contracts a month",
      features: [
        "Everything in Starter",
        "Add 50-contract packs for $399, good for 12 months",
        "Advanced compliance controls",
        "Forward-deployed engineer and custom SLAs",
      ],
      cta: "Start free trial",
      highlight: true,
      onClick: goValidate("plus"),
    },
    {
      name: "Unlimited",
      blurb: "For high-volume teams that need unlimited contracts and tighter controls.",
      price: "Custom",
      note: "Scoped to your volume",
      features: [
        "Everything in Plus",
        "Unlimited contracts a month",
        "SSO/SAML and custom compliance controls",
        "Forward-deployed engineer, onboarding, and custom SLAs",
      ],
      cta: "Contact sales",
      ctaVariant: "outline",
      onClick: talkToUs("validate-unlimited"),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Pricing | Meshly — the trust harness for your revenue"
        description="Two products, one trust harness. The Meshly Intelligence Platform finds the revenue that slips between your systems, from $2,900/mo. Meshly Validate catches risky contracts, from $299/mo. Priced by what flows through your systems, not per seat."
        canonicalUrl="https://www.meshly.ai/pricing"
        structuredData={PRICING_SERVICE_SCHEMA}
      />
      <Header />

      {/* Hero */}
      <section className="bg-background px-4 pb-10 pt-16">
        <div className="mx-auto max-w-5xl">
          <Eyebrow>Pricing</Eyebrow>
          <h1 className="max-w-3xl text-4xl font-bold leading-[1.05] tracking-[-0.02em] text-foreground md:text-5xl">
            Priced by what flows through your systems, not who logs in.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-foreground">
            Meshly finds the revenue your systems quietly lose, and proves every number that's right.
            You pay for what flows through it, not how many people log in.
          </p>
        </div>
      </section>

      {/* PRODUCT 01 — Meshly Intelligence Platform (Meshly blue, first) */}
      <section id="platform" className="scroll-mt-20 border-t border-border bg-primary py-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <Eyebrow onBlue>The full platform</Eyebrow>
            <SectionH2 onBlue>Meshly Intelligence Platform</SectionH2>
            <p className="mb-10 mt-5 max-w-2xl text-lg leading-relaxed text-white">
              Your whole revenue stack on one trust harness. Match a single deal across CRM, billing,
              ERP, and contracts, then catch the revenue that slips between them, the moment a system
              drifts.
            </p>
            <div className="grid items-stretch gap-6 md:grid-cols-2">
              {platformTiers.map((t) => (
                <TierCard key={t.name} tier={t} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT 02 — Meshly Validate (second) */}
      <section id="validate" className="scroll-mt-20 border-t border-border bg-background py-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <Eyebrow>Contract validation</Eyebrow>
            <SectionH2>Meshly Validate</SectionH2>
            <p className="mb-10 mt-5 max-w-2xl text-lg leading-relaxed text-foreground">
              The off-policy discount, the Net 90, the duration that doesn't match the order, caught
              the moment a contract lands, before it ever reaches billing.
            </p>
            <div className="grid items-stretch gap-6 md:grid-cols-3">
              {validateTiers.map((t) => (
                <TierCard key={t.name} tier={t} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How we price */}
      <section className="border-t border-border bg-secondary/40 py-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <Eyebrow>How we price</Eyebrow>
            <SectionH2>Priced to the money, not the headcount.</SectionH2>
            <p className="mt-6 text-lg leading-relaxed text-foreground">
              Per-seat pricing assumes value scales with users. Meshly's scales with your revenue. A
              number that's quietly leaking costs the same whether three people touch it or thirty,
              so you pay for the contracts and transactions that carry your money, not your headcount.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-background py-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl">
            <Eyebrow>FAQ</Eyebrow>
            <SectionH2>The questions we get</SectionH2>
            <Accordion type="single" collapsible className="mt-10 border-t border-border">
              {[
                {
                  q: "Why not charge per user?",
                  a: "Because the money doesn't leak per person. A wrong or missed number costs the same whether three people touch it or thirty. You pay for the contracts and transactions that carry your revenue.",
                },
                {
                  q: "What's the difference between the Intelligence Platform and Validate?",
                  a: "Validate checks contracts before they reach your systems. The Intelligence Platform watches every connected system and catches what slips between them, continuously. Validate Plus is included in the Platform, up to 100 contracts a month.",
                },
                {
                  q: "What if I go over my Validate contract limit?",
                  a: "No surprise charges. Add contract packs as you need them, good for 12 months. Starter: 15 contracts for $149. Plus: 50 for $399. Or move to Unlimited.",
                },
                {
                  q: "How is the Intelligence Platform priced?",
                  a: "The Platform is $2,900/mo ($34,800/yr), which covers up to 100 contracts a month across your connected systems. We do not charge per entity or per seat. Custom is for bespoke reconciliation logic, volume beyond that, or strict audit and security requirements, and we size it with you in a working session.",
                },
              ].map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                  <AccordionTrigger className="py-5 text-left text-lg font-semibold text-foreground hover:no-underline data-[state=open]:text-primary">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base leading-relaxed text-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
